import { useState, useEffect, useCallback } from 'react';
import {
  withLoadingPlaceholders,
  fetchAttributesForPerson,
  mergeAttributePrefetchRows,
} from '../persons-manager.helpers';

/**
 * When `results` changes, prefetch attributes for each person row (loading → ok/error).
 * Returns `{ attributesByPersonId, refreshPersonAttributes }`.
 */
export function usePersonResultAttributes(results) {
  const [attributesByPersonId, setAttributesByPersonId] = useState({});

  useEffect(() => {
    if (!results.length) {
      setAttributesByPersonId({});
      return undefined;
    }

    const personIds = results.map((r) => r.personId);
    setAttributesByPersonId((prev) => withLoadingPlaceholders(prev, personIds));

    let cancelled = false;

    (async () => {
      const rows = await Promise.all(results.map((p) => fetchAttributesForPerson(p.personId)));
      if (cancelled) return;
      setAttributesByPersonId((prev) => mergeAttributePrefetchRows(prev, rows));
    })();

    return () => {
      cancelled = true;
    };
  }, [results]);

  const refreshPersonAttributes = useCallback(async (personId) => {
    const row = await fetchAttributesForPerson(personId);
    setAttributesByPersonId((prev) => ({
      ...prev,
      [personId]: { status: row.ok ? 'ok' : 'error', items: row.items },
    }));
  }, []);

  return { attributesByPersonId, refreshPersonAttributes };
}
